import React, { useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import useShippingRequest, { shippingAccessScope } from '../../hooks/useShippingRequest';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import { moneyMinor } from '../../utils/money';
import { useT } from '../../i18n/LanguageContext';
import DistributorMerchantAdverseReview from './DistributorMerchantAdverseReview';
import {
  fundingDay,
  fundingReference,
  matchesFundingIntent,
  readMerchantFunding,
} from './merchantFundingReceipt';

/** Records evidence only. Independent backend observation still decides unit credit. */
export default function DistributorMerchantFunding({
  distributorId,
  purchase,
  disabled,
  onPendingChange,
  onRefresh,
}) {
  const t = useT();
  const [value, setValue] = useState(null);
  const [reference, setReference] = useState('');
  const [day, setDay] = useState('');
  const [funded, setFunded] = useState(false);
  const [included, setIncluded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [intent, setIntent] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [adversePending, setAdversePending] = useState(false);
  const pending = useRef(false);
  const frozen = useRef(null);
  const session = useRef(captureSession());
  const scope = useRef(shippingAccessScope());
  const { begin, version } = useShippingRequest(`${distributorId}:${purchase._id}`);
  useEffect(() => {
    setValue(null);
    setReference('');
    setDay('');
    setFunded(false);
    setIncluded(false);
    setIntent(null);
    setError('');
    setNotice('');
    setBusy(false);
    setAdversePending(false);
    pending.current = false;
    frozen.current = null;
  }, [version, distributorId, purchase._id]);
  const current = () =>
    isSessionCurrent(session.current) && scope.current === shippingAccessScope();
  const draft = Boolean(reference || day || funded || included);
  const load = async () => {
    if (!current() || pending.current || disabled || adversePending) return;
    pending.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    setValue(null);
    onPendingChange?.(true);
    const request = begin();
    try {
      const next = readMerchantFunding(
        await distributorService.merchantFunding(distributorId, purchase._id, {
          signal: request.signal,
        }),
        purchase
      );
      if (!request.isCurrent() || !current()) return;
      if (
        frozen.current &&
        next.merchantFunding &&
        !matchesFundingIntent(next, frozen.current, session.current.userId)
      )
        setNotice('Another confirmation is already recorded. Review the saved evidence.');
      setValue(next);
      frozen.current = null;
      setIntent(null);
      setReference('');
      setDay('');
      setFunded(false);
      setIncluded(false);
      onPendingChange?.(false);
      if (next.merchantFunding) onRefresh?.();
    } catch {
      if (request.isCurrent() && current()) {
        setError(
          'Funding details are unavailable. Check the saved confirmation before continuing.'
        );
        if (!frozen.current) onPendingChange?.(false);
      }
    } finally {
      if (request.isCurrent() && current()) {
        pending.current = false;
        setBusy(false);
      }
    }
  };
  const submit = async (event) => {
    event.preventDefault();
    if (
      !current() ||
      pending.current ||
      disabled ||
      frozen.current ||
      !value?.canConfirm ||
      !fundingReference(reference) ||
      !fundingDay(day) ||
      !funded ||
      !included
    )
      return;
    const requestKey = window.crypto?.randomUUID?.();
    if (!requestKey || !/^[A-Za-z0-9_-]{16,100}$/.test(requestKey)) {
      setError('A secure request key is unavailable. Reload before reviewing.');
      return;
    }
    const captured = {
      requestKey,
      expectedAttemptRevision: value.expectedAttemptRevision,
      realmId: value.realmId,
      transactionId: value.transactionId,
      amountCents: value.amountCents,
      currency: 'USD',
      depositReference: reference,
      fundedDate: day,
      confirmedFunded: true,
      confirmedPaymentIncluded: true,
    };
    frozen.current = captured;
    setIntent(captured);
    pending.current = true;
    setBusy(true);
    setError('');
    onPendingChange?.(true);
    const request = begin();
    try {
      const next = readMerchantFunding(
        await distributorService.confirmMerchantFunding(distributorId, purchase._id, captured),
        purchase
      );
      if (!request.isCurrent() || !current()) return;
      if (!matchesFundingIntent(next, captured, session.current.userId))
        throw new Error('Unconfirmed funding evidence');
      setValue(next);
      frozen.current = null;
      setIntent(null);
      onPendingChange?.(false);
      onRefresh?.();
    } catch {
      if (request.isCurrent() && current())
        setError(
          'Confirmation result is unconfirmed. Check the saved confirmation; nothing will be resent automatically.'
        );
    } finally {
      if (request.isCurrent() && current()) {
        pending.current = false;
        setBusy(false);
      }
    }
  };
  const change = (setter, next) => {
    if (!current() || pending.current || frozen.current || disabled) return;
    setter(next);
    onPendingChange?.(true);
  };
  return (
    <div className="mt-3 space-y-3">
      <button
        type="button"
        disabled={disabled || busy || adversePending || (draft && !intent)}
        onClick={load}
      >
        {t(intent ? 'Check saved confirmation' : 'Review merchant funding')}
      </button>
      {busy && <p role="status">{t('Checking merchant funding...')}</p>}
      {error && <p role="alert">{t(error)}</p>}
      {notice && <p role="status">{t(notice)}</p>}
      {value && (
        <>
          <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
            <div>
              <dt>{t('Payment amount')}</dt>
              <dd>{moneyMinor(value.amountCents)} USD</dd>
            </div>
            <div>
              <dt>{t('Payment transaction')}</dt>
              <dd className="break-all">{value.transactionId}</dd>
            </div>
            <div>
              <dt>{t('QuickBooks company ID')}</dt>
              <dd className="break-all">{value.realmId}</dd>
            </div>
          </dl>
          <p>
            {t(
              'Confirm the matching payment inside a Funded deposit in QuickBooks Merchant Service Center. This records evidence only; units remain pending until MedEffects verifies the payment again.'
            )}
          </p>
          {value.merchantFunding ? (
            <p role="status">
              {t('Merchant funding confirmed')}: {value.merchantFunding.depositReference} ·{' '}
              {value.merchantFunding.fundedDate}
            </p>
          ) : value.canConfirm ? (
            <form onSubmit={submit} className="space-y-3">
              <fieldset
                disabled={disabled || busy || Boolean(intent)}
                className="grid gap-3 sm:grid-cols-2"
              >
                <label>
                  {t('Merchant deposit reference')}
                  <input
                    className="block w-full rounded border p-2"
                    maxLength={120}
                    value={reference}
                    onChange={(event) => change(setReference, event.target.value)}
                  />
                </label>
                <label>
                  {t('Funding date shown in QuickBooks')}
                  <input
                    className="block w-full rounded border p-2"
                    type="date"
                    value={day}
                    onChange={(event) => change(setDay, event.target.value)}
                  />
                </label>
                <label className="sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={funded}
                    onChange={(event) => change(setFunded, event.target.checked)}
                  />{' '}
                  {t('I verified the merchant deposit status is Funded.')}
                </label>
                <label className="sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={included}
                    onChange={(event) => change(setIncluded, event.target.checked)}
                  />{' '}
                  {t(
                    'I verified this exact transaction and USD amount are included in that deposit.'
                  )}
                </label>
              </fieldset>
              <p className="text-sm">
                {t(
                  'Use the calendar date displayed by QuickBooks. Do not enter bank details, credentials or private payment responses.'
                )}
              </p>
              <button
                type="submit"
                className="me-distributor-admin-primary"
                disabled={
                  disabled ||
                  busy ||
                  Boolean(intent) ||
                  !fundingReference(reference) ||
                  !fundingDay(day) ||
                  !funded ||
                  !included
                }
              >
                {t('Confirm merchant funding')}
              </button>
              {!intent && (
                <button type="button" disabled={disabled || busy} onClick={load}>
                  {t('Discard funding draft')}
                </button>
              )}
            </form>
          ) : (
            <p>{t('Funding cannot be confirmed for this payment yet.')}</p>
          )}
          {value.merchantFunding && (
            <DistributorMerchantAdverseReview
              distributorId={distributorId}
              purchase={purchase}
              disabled={disabled || busy || Boolean(intent)}
              onPendingChange={(next) => {
                setAdversePending(next);
                onPendingChange?.(next);
              }}
              onRefresh={onRefresh}
            />
          )}
        </>
      )}
    </div>
  );
}
